import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class EcwidSettings extends ModelObject<EcwidSettings> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _defaultDeliveryStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for delivery charge of imported orders. This will be used if no BrewMan product name matches the Shipping Option Name in Ecwid
    */
    public get defaultDeliveryStockItemId(): Guid | null { return this._defaultDeliveryStockItemId }
    public set defaultDeliveryStockItemId(value: Guid | null) {
      this._defaultDeliveryStockItemId = value === undefined ? null : value;
    }
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then Orders on Ecwid from before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Outlet Id for all Ecwid orders import to a single outlet. Should be null only if tenant doesn't use Ecwid.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _priceListId: Guid | null = null;
    /**
    * PriceList Id. If provided then products on Ecwid will be updated with the List Price in BrewMan.
    */
    public get priceListId(): Guid | null { return this._priceListId }
    public set priceListId(value: Guid | null) {
      this._priceListId = value === undefined ? null : value;
    }
    private _stockLocationId: Guid | null = null;
    /**
    * StockLocation Id. If provided then product on Ecwid will be updated with the Stock Location's stock level in BrewMan.
    */
    public get stockLocationId(): Guid | null { return this._stockLocationId }
    public set stockLocationId(value: Guid | null) {
      this._stockLocationId = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    private _storeId: number | null = null;
    /**
    * The Ecwid store id. Used to uniquely identify this integration.
    */
    public get storeId(): number | null { return this._storeId }
    public set storeId(value: number | null) {
      this._storeId = value === undefined ? null : value;
    }
    private _defaultGratuityStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for gratuity of imported orders. This will be used if no BrewMan product name matches the surcharges in Ecwid
    */
    public get defaultGratuityStockItemId(): Guid | null { return this._defaultGratuityStockItemId }
    public set defaultGratuityStockItemId(value: Guid | null) {
      this._defaultGratuityStockItemId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.defaultDeliveryStockItemId = jsonSerializedObject.defaultDeliveryStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultDeliveryStockItemId);
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.priceListId = jsonSerializedObject.priceListId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.stockLocationId = jsonSerializedObject.stockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.storeId = jsonSerializedObject.storeId == null ? null : jsonSerializedObject.storeId;
          this.defaultGratuityStockItemId = jsonSerializedObject.defaultGratuityStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultGratuityStockItemId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.defaultDeliveryStockItemId = this.defaultDeliveryStockItemId == null ? null : this.defaultDeliveryStockItemId.toString();
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.priceListId = this.priceListId == null ? null : this.priceListId.toString();
        toRet.stockLocationId = this.stockLocationId == null ? null : this.stockLocationId.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.storeId = this.storeId == null ? null : this.storeId;
        toRet.defaultGratuityStockItemId = this.defaultGratuityStockItemId == null ? null : this.defaultGratuityStockItemId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "EcwidSettings.Id cannot be empty"},
            {field: "defaultDeliveryStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "EcwidSettings.DefaultDeliveryStockItemId cannot be empty"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "EcwidSettings.OutletId cannot be empty"},
            {field: "priceListId", rule: (v: any) => v == null || !v.isEmpty() || "EcwidSettings.PriceListId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "EcwidSettings.StockLocationId cannot be empty"},
            {field: "storeId", rule: (v: any) => v == null || v >= -2147483648 || "StoreId is not greater than minimum allowed value: -2147483648"},
            {field: "storeId", rule: (v: any) => v == null || v <= 2147483647 || "StoreId is above maximum allowed value: 2147483647"},
            {field: "defaultGratuityStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "EcwidSettings.DefaultGratuityStockItemId cannot be empty"},
    ];
}


export class TenantSettingsId extends ModelObject<TenantSettingsId> {

    public tenantId: Guid = Guid.createEmpty();
    public ecwidSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.ecwidSettingsId = Guid.parseOrEmpty(jsonSerializedObject.ecwidSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.ecwidSettingsId = this.ecwidSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.TenantId cannot be empty"},
            {field: "ecwidSettingsId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.EcwidSettingsId cannot be empty"},
    ];
}


export class EcwidSettingsService extends ServiceBase {

  public async getEcwidSettings(ecwidSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EcwidSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecwidSettingsId:ecwidSettingsId.toString(),
    };
    const extractor = (response:any) => new EcwidSettings(response.ecwidSettings);;
    return this.makeJsonRequest("ecwidSettings/v1/getEcwidSettings", req, false, extractor, opts);
  };

  public async getOrCreateEcwidSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EcwidSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new EcwidSettings(response.ecwidSettings);;
    return this.makeJsonRequest("ecwidSettings/v1/getOrCreateEcwidSettings", req, false, extractor, opts);
  };

  public async saveEcwidSettings(ecwidSettings: EcwidSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecwidSettings:ecwidSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("ecwidSettings/v1/saveEcwidSettings", req, true, extractor, opts);
  };

  public async hardDeleteEcwidSettings(ecwidSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecwidSettingsId:ecwidSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("ecwidSettings/v1/hardDeleteEcwidSettings", req, true, extractor, opts);
  };

  public async createEcwidLoginUrl(ecwidSettingsId: Guid, returnPageUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecwidSettingsId:ecwidSettingsId.toString(),
    returnPageUrl:returnPageUrl,
    };
    const extractor = (response:any) => response.url;;
    return this.makeJsonRequest("ecwidSettings/v1/createEcwidLoginUrl", req, false, extractor, opts);
  };

  public async completeEcwidAuthentication(ecwidSettingsId: Guid, queryString: string, returnPageUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EcwidSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecwidSettingsId:ecwidSettingsId.toString(),
    queryString:queryString,
    returnPageUrl:returnPageUrl,
    };
    const extractor = (response:any) => new EcwidSettings(response.ecwidSettings);;
    return this.makeJsonRequest("ecwidSettings/v1/completeEcwidAuthentication", req, true, extractor, opts);
  };

  public async isEcwidAuthenticated(ecwidSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecwidSettingsId:ecwidSettingsId.toString(),
    };
    const extractor = (response:any) => response.authenticated;;
    return this.makeJsonRequest("ecwidSettings/v1/isEcwidAuthenticated", req, false, extractor, opts);
  };

  public async removeEcwidAuthentication(ecwidSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ecwidSettingsId:ecwidSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("ecwidSettings/v1/removeEcwidAuthentication", req, true, extractor, opts);
  };
}

export const ecwidSettingsService = new EcwidSettingsService();